/**
 * Trends Module - Complete Implementation
 * Handles all trending data visualization and analysis
 * @module dashboard/modules/trends
 */

import { dashboardState } from '../core/state.js';
import { secureFetch, getAPIBase } from '../core/api.js';
import { formatDate } from '../core/utils.js';

export class TrendsModule {
    constructor(config = {}) {
        this.config = config;
        this.chartInstance = null;
        this.currentMetric = 'weight';
        this.currentRange = '30d';
    }

    /**
     * Initialize trends module
     */
    async init() {
        console.log('📈 Trends Module initialized');
    }

    /**
     * Fetch trend data from API
     * @param {string} userId - User ID
     * @param {string} metric - Metric to fetch (weight, bmi, steps, etc.)
     * @param {string} range - Time range (7d, 30d, 90d, etc.)
     * @returns {Promise<Object>} Trend data
     */
    async fetchTrendData(userId, metric = 'weight', range = '30d') {
        try {
            const url = `${getAPIBase()}/api/trends/${userId}?metric=${metric}&range=${range}`;
            const res = await secureFetch(url);
            const data = await res.json();

            dashboardState.setRawData(`trends_${metric}`, data);
            return data;
        } catch (error) {
            console.error('Error fetching trend data:', error);
            throw error;
        }
    }

    /**
     * Render trend cards with statistics
     * @param {HTMLElement} container - Container element
     * @param {Object} trendData - Trend data object
     */
    renderTrendCards(container, trendData) {
        if (!trendData || !trendData.periods) {
            container.innerHTML = '<p>No trend data available</p>';
            return;
        }

        const { periods } = trendData;
        const html = `
            <div class="trend-cards-grid">
                ${Object.entries(periods).map(([period, stats]) => `
                    <div class="trend-card">
                        <div class="trend-period">${this.formatPeriod(period)}</div>
                        <div class="trend-value ${stats.change >= 0 ? 'positive' : 'negative'}">
                            ${stats.change >= 0 ? '+' : ''}${stats.change.toFixed(1)}
                        </div>
                        <div class="trend-label">
                            ${stats.percentChange >= 0 ? '+' : ''}${stats.percentChange.toFixed(1)}%
                        </div>
                    </div>
                `).join('')}
            </div>
        `;

        container.innerHTML = html;
    }

    /**
     * Render trend summary
     * @param {HTMLElement} container - Container element
     * @param {Object} trendData - Trend data object
     */
    renderTrendSummary(container, trendData) {
        if (!trendData || !trendData.summary) {
            container.innerHTML = '<p>No summary available</p>';
            return;
        }

        const { summary } = trendData;
        const html = `
            <div class="trend-summary">
                <h3>Overall Trend</h3>
                <div class="summary-stat">
                    <span>Direction:</span>
                    <span class="${summary.direction}">${summary.direction}</span>
                </div>
                <div class="summary-stat">
                    <span>Average Change:</span>
                    <span>${summary.avgChange.toFixed(2)} per day</span>
                </div>
                <div class="summary-stat">
                    <span>Total Change:</span>
                    <span>${summary.totalChange.toFixed(1)}</span>
                </div>
            </div>
        `;

        container.innerHTML = html;
    }

    /**
     * Calculate rolling average
     * @param {Array<number>} data - Data array
     * @param {number} window - Window size
     * @returns {Array<number>} Rolling averages
     */
    calculateRollingAverage(data, window = 7) {
        const result = [];
        for (let i = 0; i < data.length; i++) {
            const start = Math.max(0, i - window + 1);
            const subset = data.slice(start, i + 1);
            const avg = subset.reduce((a, b) => a + b, 0) / subset.length;
            result.push(avg);
        }
        return result;
    }

    /**
     * Filter data by time range
     * @param {Array} scaleData - Scale data
     * @param {Array} activityData - Activity data
     * @param {Array} sleepData - Sleep data
     * @param {string} range - Time range
     * @returns {Object} Filtered data
     */
    filterByRange(scaleData, activityData, sleepData, range) {
        const now = new Date();
        let cutoffDate;

        const dayMs = 24 * 60 * 60 * 1000;

        switch (range) {
            case '7d':
                cutoffDate = new Date(now.getTime() - (7 * dayMs));
                break;
            case '14d':
                cutoffDate = new Date(now.getTime() - (14 * dayMs));
                break;
            case '30d':
                cutoffDate = new Date(now.getTime() - (30 * dayMs));
                break;
            case '90d':
                cutoffDate = new Date(now.getTime() - (90 * dayMs));
                break;
            case '1y':
                cutoffDate = new Date(now.getTime() - (365 * dayMs));
                break;
            default:
                cutoffDate = new Date(0); // All time
        }

        console.log(`📈 TrendsModule filtering with cutoff: ${cutoffDate.toLocaleDateString()} (range: ${range})`);

        return {
            scaleData: scaleData.filter(d => new Date(d.timestamp || d.date).getTime() >= cutoffDate.getTime()),
            activityData: activityData.filter(d => new Date(d.date || d.timestamp).getTime() >= cutoffDate.getTime()),
            sleepData: sleepData.filter(d => new Date(d.date || d.timestamp || d.start_time).getTime() >= cutoffDate.getTime())
        };
    }

    /**
     * Render correlation chart
     * @param {HTMLElement} canvas - Canvas element
     * @param {Array} scaleData - Scale data
     * @param {Array} metricData - Metric data to correlate
     * @param {string} metricName - Name of metric
     */
    renderCorrelationChart(canvas, scaleData, metricData, metricName) {
        // Implementation for correlation chart
        console.log('Rendering correlation chart:', metricName);
    }

    /**
     * Format period label
     * @param {string} period - Period key
     * @returns {string} Formatted label
     */
    formatPeriod(period) {
        const labels = {
            '7d': 'Last 7 Days',
            '14d': 'Last 14 Days',
            '30d': 'Last 30 Days',
            '90d': 'Last 90 Days'
        };
        return labels[period] || period;
    }

    /**
     * Render graph (Ported from legacy with enhancements)
     * @param {HTMLElement|string} canvas - Canvas element or ID
     * @param {string} mode - Graph mode ('sleep_steps', 'correlation')
     * @param {Object} options - Additional options
     */
    async renderGraph(canvas, mode = 'sleep_steps', options = {}) {
        const canvasEl = typeof canvas === 'string' ? document.getElementById(canvas) : canvas;
        if (!canvasEl) return;

        const { range = '30d', userId = dashboardState.getState().user?.id } = options;

        // Ensure state has data
        const state = dashboardState.getState();
        const rawScale = [...(state.rawData.scale || [])];
        const rawActivity = [...(state.rawData.activity || [])];
        const rawSleep = [...(state.rawData.sleep || [])];

        // If insufficient data for a trend (need at least 2 points), fetch history
        if (rawScale.length < 2 && userId) {
            console.log('TrendsModule: Insufficient scale data for trend, fetching history...');
            try {
                // Fetch 90 days of history for a good trend line
                const res = await secureFetch(`${getAPIBase()}/users/${userId}/scale?days=90`);
                const json = await res.json();
                if (json.data && json.data.length > 0) {
                    dashboardState.setRawData('scale', json.data);
                    rawScale.length = 0; // Clear the old single-point array
                    rawScale.push(...json.data);
                }
            } catch (e) {
                console.error('TrendsModule: Failed auto-fetch scale history:', e);
            }
        }

        if (rawActivity.length < 2 && userId) {
            console.log('TrendsModule: Insufficient activity data for trend, fetching history...');
            try {
                const res = await secureFetch(`${getAPIBase()}/api/activity/history?user_id=${userId}&days=90`);
                const json = await res.json();
                if (json.activity) {
                    dashboardState.setRawData('activity', json.activity);
                    rawActivity.length = 0;
                    rawActivity.push(...json.activity);
                }
                if (json.sleep) {
                    dashboardState.setRawData('sleep', json.sleep);
                    rawSleep.length = 0;
                    rawSleep.push(...json.sleep);
                }
            } catch (e) {
                console.error('TrendsModule: Failed auto-fetch activity history:', e);
            }
        }

        const { scaleData, activityData, sleepData } = this.filterByRange(rawScale, rawActivity, rawSleep, range);
        console.log('📈 TrendsModule.renderGraph dataset sizes:', {
            scale: scaleData.length,
            activity: activityData.length,
            sleep: sleepData.length
        });

        const ctx = canvasEl.getContext('2d');

        // Destroy existing instance
        if (this.chartInstance) {
            this.chartInstance.destroy();
        }

        let labels = [];
        let datasets = [];
        let chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 750, easing: 'easeOutQuart' },
            interaction: {
                mode: 'index',
                intersect: false,
            },
            plugins: {
                legend: {
                    display: true,
                    labels: { color: '#94a3b8', font: { size: 10 } }
                },
                tooltip: {
                    backgroundColor: 'rgba(15, 23, 42, 0.9)',
                    titleColor: '#fff',
                    bodyColor: '#cbd5e1',
                    padding: 12,
                    borderRadius: 8
                }
            },
            scales: {
                x: {
                    grid: { display: false },
                    ticks: { color: '#64748b', font: { size: 10 } }
                }
            }
        };

        if (mode === 'sleep_steps' || mode === 'correlation') {
            const dateMap = {};
            const metric = mode === 'sleep_steps' ? { label: 'Steps', key: 'steps', color: '#10b981' } : { label: 'Metric', key: 'metric', color: '#3b82f6' };

            // Base weight data
            scaleData.forEach(s => {
                const dateString = s.timestamp || s.date;
                if (!dateString) return;
                const date = new Date(dateString).toLocaleDateString();
                if (!dateMap[date]) dateMap[date] = { weight: null, metric: null, sleep: null };

                // Handle nested object structure from main.py health-overview
                const weight = s.weight_lbs?.value !== undefined ? s.weight_lbs.value :
                    (s.weight_lbs !== undefined ? s.weight_lbs : s.weight);
                dateMap[date].weight = weight;
            });

            // Activity data
            activityData.forEach(a => {
                const dateString = a.date || a.timestamp;
                if (!dateString) return;
                const date = new Date(dateString).toLocaleDateString();
                if (!dateMap[date]) dateMap[date] = { weight: null, metric: null, sleep: null };
                dateMap[date].metric = a.steps || 0;
            });

            // Sleep data
            sleepData.forEach(s => {
                const dateString = s.date || s.timestamp || s.start_time;
                if (!dateString) return;
                const date = new Date(dateString).toLocaleDateString();
                if (!dateMap[date]) dateMap[date] = { weight: null, metric: null, sleep: null };

                const duration = s.duration_minutes || s.duration || s.sleep_minutes || 0;
                dateMap[date].sleep = duration / 60;
            });

            labels = Object.keys(dateMap).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
            const weightValues = labels.map(l => dateMap[l].weight || null);
            const mValues = labels.map(l => mode === 'sleep_steps' ? dateMap[l].sleep : dateMap[l].metric);

            const weightAvg = this.calculateRollingAverage(weightValues, 7);
            const mAvg = this.calculateRollingAverage(mValues, 7);

            datasets = [
                {
                    label: 'Weight (lb)',
                    data: weightValues,
                    borderColor: 'rgba(139, 92, 246, 0.3)',
                    backgroundColor: 'transparent',
                    yAxisID: 'y',
                    borderWidth: 1,
                    pointRadius: 2,
                    tension: 0.4,
                    spanGaps: true
                },
                {
                    label: 'Weight (7d avg)',
                    data: weightAvg,
                    borderColor: '#8b5cf6',
                    backgroundColor: 'transparent',
                    yAxisID: 'y',
                    borderWidth: 3,
                    pointRadius: 0,
                    tension: 0.4,
                    spanGaps: true
                },
                {
                    label: mode === 'sleep_steps' ? 'Sleep (hrs)' : 'Steps',
                    data: mValues,
                    borderColor: `${metric.color}50`,
                    backgroundColor: 'transparent',
                    yAxisID: 'y1',
                    borderWidth: 1,
                    pointRadius: 2,
                    tension: 0.4,
                    spanGaps: true
                },
                {
                    label: `${mode === 'sleep_steps' ? 'Sleep' : 'Steps'} (7d avg)`,
                    data: mAvg,
                    borderColor: metric.color,
                    backgroundColor: 'transparent',
                    yAxisID: 'y1',
                    borderWidth: 3,
                    pointRadius: 0,
                    tension: 0.4,
                    spanGaps: true
                }
            ];

            chartOptions.scales.y = {
                type: 'linear',
                display: true,
                position: 'left',
                title: { display: true, text: 'Weight (lb)', color: '#8b5cf6' },
                grid: { color: 'rgba(255, 255, 255, 0.05)' }
            };
            chartOptions.scales.y1 = {
                type: 'linear',
                display: true,
                position: 'right',
                title: { display: true, text: mode === 'sleep_steps' ? 'Hours' : 'Steps', color: metric.color },
                grid: { drawOnChartArea: false }
            };
        } else if (mode === 'smooth') {
            labels = scaleData.map(d => new Date(d.timestamp).toLocaleDateString());
            const weights = scaleData.map(d => d.weight_lbs);
            const smoothed = weights.map((v, i, arr) => {
                if (i < 2) return v;
                return (arr[i] + arr[i - 1] + arr[i - 2]) / 3;
            });

            datasets = [{
                label: 'Weight Trend',
                data: smoothed,
                borderColor: '#8b5cf6',
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                tension: 0.5,
                fill: true,
                pointRadius: 0
            }];
        } else if (mode === 'weekly') {
            const weeklyMap = {};
            scaleData.forEach(d => {
                const date = new Date(d.timestamp);
                const week = `Week ${Math.ceil(date.getDate() / 7)} - ${date.toLocaleString('default', { month: 'short' })}`;
                if (!weeklyMap[week]) weeklyMap[week] = { sum: 0, count: 0 };
                weeklyMap[week].sum += d.weight_lbs;
                weeklyMap[week].count++;
            });

            labels = Object.keys(weeklyMap).slice(-5);
            const averages = labels.map(l => (weeklyMap[l].sum / weeklyMap[l].count).toFixed(1));

            datasets = [{
                label: 'Avg Weight',
                data: averages,
                backgroundColor: 'rgba(139, 92, 246, 0.6)',
                borderRadius: 8
            }];
            chartOptions.scales.y = { beginAtZero: false };
        }

        this.chartInstance = new Chart(ctx, {
            type: 'line',
            data: { labels, datasets },
            options: chartOptions
        });
    }

    /**
     * Destroy module and clean up
     */
    destroy() {
        if (this.chartInstance) {
            this.chartInstance.destroy();
            this.chartInstance = null;
        }
        console.log('🗑️ Trends Module destroyed');
    }
}

// Export for global access
window.TrendsModule = TrendsModule;
